import { useQuery } from '@apollo/client';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import React, { useState } from 'react';
import Confetti from 'react-confetti';
import useWindowSize from 'react-use/lib/useWindowSize';
import { ROOTURL } from '../../config';
import { getBadge } from '../../helpers/getBadge';
import { GET_BADGES } from '../../helpers/graphql/profile';
import { getUser } from '../../helpers/token';
import Link from '@mui/material/Link';
import BadgeStamp from '../Badges/BadgeStamp';
import PostSocialShares from '../Post/PostSocialShares';

const sample = require('lodash.sample');

export default function NewBadgePopup() {
  const { data } = useQuery(GET_BADGES, {
    variables: { user: getUser(), onlyUnseen: true },
    ssr: false,
    fetchPolicy: 'no-cache',
  });

  const [open, setOpen] = useState(true);

  const handleClose = () => {
    setOpen(false);
  };

  const { width, height } = useWindowSize();

  if (!open || !data || !data.getBadges || data.getBadges.length === 0)
    return <></>;

  const badgeType = data.getBadges[0].type;
  const badgeName = data.getBadges[0].name;

  const badge = getBadge(badgeType, badgeName);

  const badgeDescription = badge.verb || badge.description || badge.name;
  let badgeTitle = `Celebrating ${badgeDescription
    .substring(0, 1)
    .toLowerCase()}${badgeDescription.substring(1, badgeDescription.length)}`;
  if (badgeType === 'country' || badgeType === 'region')
    badgeTitle = `Celebrating blogging about ${badge.name}`;

  const generatedOgImage = `https://og.travelfeed.com/api/badge?user=${getUser()}&title=${encodeURIComponent(
    badgeTitle,
  )}${
    badge.emoji
      ? `&emoji=${badge.emoji}`
      : `&image=${ROOTURL}/img/badges/${badge.name}.png`
  }`;

  return (
    <>
      <Confetti width={width} height={height} />
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle className="text-center" id="form-dialog-title">
          {sample([
            'You rock!',
            'Awesome work!',
            'Congratulations!',
            'You are amazing!',
          ])}
          <div
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              padding: '2px',
            }}
          >
            <IconButton onClick={handleClose} aria-label="Close" size="large">
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent>
          <FormLabel component="legend" className="text-center">
            Your TravelFeed blog is growing!{' '}
            {data.getBadges.length === 1
              ? 'You earned a new badge'
              : `You earned ${data.getBadges.length} new badges`}
          </FormLabel>
          <div className="tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-4">
            {data.getBadges.map(oneBadge => {
              // Unknown names (e.g. badges newer than this build) render
              // nothing instead of a broken stamp.
              const stampBadge = getBadge(oneBadge.type, oneBadge.name);
              if (!stampBadge) return null;
              return (
                <Link
                  key={oneBadge.name}
                  href={`/@${getUser()}/badges/${oneBadge.type}/${
                    oneBadge.name
                  }`}
                >
                  <BadgeStamp
                    badge={stampBadge}
                    type={oneBadge.type}
                    text={`TRAVELFEED · ${oneBadge.type.toUpperCase()} STAMP · `}
                    className="tw-h-32 tw-w-32 -tw-rotate-6 tw-text-primary-700 dark:tw-text-primary-300"
                  />
                </Link>
              );
            })}
          </div>
          <Typography variant="h6" className="text-center mt-3">
            Share your achievement with your friends:
          </Typography>
          <PostSocialShares
            fullLink={`@${getUser()}/badges/${data.getBadges[0].type}/${
              data.getBadges[0].name
            }`}
            title={`I earned a new badge: ${badge.description || badge.name}!`}
            thumbnail={generatedOgImage}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
